import * as mapUtils from './mapUtils'
import * as googleMaps from '@/common/googleMaps'
import { MarkerClusterer, SuperClusterAlgorithm, defaultOnClusterClickHandler } from '@googlemaps/markerclusterer'

const fleetMarkerProps = {
  url: require('@/assets/svg/dot-01-orange.svg'),
  width: 45,
  height: 45,
  scaledWidth: 45,
  scaledHeight: 45,
  rotational: false,
  headingOffset: 0
}

const clusterWidth = 30
const clusterHeight = 30
const clusterX = clusterWidth / 2
const clusterY = clusterWidth / 2
const clusterR = clusterWidth / 2
const clusterColor = '#F57C00'
const clusterFontSize = 14

let fleetMarkers = new Map()
let markerClickHandlers = new Map()
let markerClusterer = null

const getClusterSVG = (count) => {
  const svg = `
    <svg width="${clusterWidth}" height="${clusterHeight}" viewBox="0 0 ${clusterWidth} ${clusterHeight}" xmlns="http://www.w3.org/2000/svg">
      <circle cx="${clusterX}" cy="${clusterY}" r="${clusterR}" fill="${clusterColor}" />
      <text x="50%" y="50%" text-anchor="middle" fill="white" font-size="${clusterFontSize}" dy=".3em">${count}</text>
    </svg>`
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg)
}

/**
 * Adds a vehicle/unit marker click handler with a specified identifier. If
 * the event handler with the identifier is already set, nothing will
 * be done.
 *
 * @param handlerId String
 * @param onClick function
 */
export const addMarkerClickHandler = (handlerId, onClick) => {
  if (!markerClickHandlers.has(handlerId)) {
    markerClickHandlers.set(handlerId, onClick)
  }
}

/**
 * Removes an existing marker click handler.
 *
 * @param handlerId String
 */
export const removeMarkerClickHandler = (handlerId) => {
  markerClickHandlers.delete(handlerId)
}

/**
 * Removes all marker click handlers.
 */
export const clearMarkerClickHandlers = () => {
  markerClickHandlers.clear()
}

/**
 * Creates or updates a fleet vehicle marker.
 *
 * @param map google.maps.Map
 * @param assetId Integer
 * @param data Object Data to be used for the marker
 * @param labelContent null|string
 * @param options Object
 */
export const setFleetMarker = (map, assetId, data, labelContent = null, options = {}) => {
  if (fleetMarkers.has(assetId)) {
    // Update the existing marker
    let marker = fleetMarkers.get(assetId)
    const position = new google.maps.LatLng(data.latitude, data.longitude)
    marker.setPosition(position)

    let img = document.querySelector('img[src="' + marker.iconUrl + '"]')
    if (img) {
      let heading
      if (fleetMarkerProps.rotational) {
        heading = data.heading - fleetMarkerProps.headingOffset
      } else {
        heading = fleetMarkerProps.headingOffset
      }
      const originX = fleetMarkerProps.scaledWidth / 2
      const originY = fleetMarkerProps.scaledHeight / 2
      img.style.transform = `rotate(${heading}deg)`
      img.style.transformOrigin = `${originX}px ${originY}px`
    }

    if (marker.markerLabel !== null) {
      if (labelContent !== null) {
        // marker.markerLabelContent = labelContent
        marker.markerLabel.el.innerHTML = labelContent
        marker.markerLabel.el.zIndex = 10
      } else {
        // marker.markerLabelContent = null
      }

      marker.markerLabel.position = position
    }
  } else {
    // Create a new marker
    const iconUrl = fleetMarkerProps.url
    const uniqueIconUrl = `${iconUrl}#${assetId}`

    let position = new google.maps.LatLng(data.latitude, data.longitude)

    let marker = new google.maps.Marker({
      position: position,
      map: map,
      optimized: false,
      zIndex: 10,
      icon: {
        url: uniqueIconUrl,
        // anchor must be half of the scaled size
        anchor: new google.maps.Point(fleetMarkerProps.scaledWidth / 2, fleetMarkerProps.scaledHeight / 2),
        size: new google.maps.Size(fleetMarkerProps.width, fleetMarkerProps.height),
        scaledSize: new google.maps.Size(fleetMarkerProps.scaledWidth, fleetMarkerProps.scaledHeight)
      }
    })

    marker.assetId = assetId
    marker.iconUrl = uniqueIconUrl
    marker.safeZone = null

    markerClickHandlers.forEach((onClick) => {
      google.maps.event.addListener(marker, 'click', onClick)
    })

    if (labelContent !== null) {
      // Note: Calling this function will add a markerLabel property
      // to the marker object containing the label element
      // marker.markerLabelContent = labelContent
      mapUtils.createMarkerLabel(map, position, labelContent, marker, options)
    } else {
      marker.markerLabel = null
      // marker.markerLabelContent = null
    }

    fleetMarkers.set(assetId, marker)

    let heading
    if (fleetMarkerProps.rotational) {
      heading = data.heading - fleetMarkerProps.headingOffset
    } else {
      heading = fleetMarkerProps.headingOffset
    }

    const originX = fleetMarkerProps.scaledWidth / 2
    const originY = fleetMarkerProps.scaledHeight / 2

    // This code is an to attempt to adjust the icon to the correct angle
    // shortly after it is shown for the first time on screen. Google maps
    // loads the icon asynchronously and only once or if its visibility is set
    // to true. This is only really required for the first time the image has
    // loaded as the images are cached.
    let displayIntervalCount = 0
    let displayInterval = setInterval(() => {
      let img = document.querySelector('img[src="' + marker.iconUrl + '"]')
      if (img) {
        img.style.transform = `rotate(${heading}deg)`
        img.style.transformOrigin = `${originX}px ${originY}px`
        clearInterval(displayInterval)
      } else if (displayIntervalCount > 40) {
        // We waited longer than a second, cancel the timer
        clearInterval(displayInterval)
      }

      displayIntervalCount += 1
    }, 25)
  }
}

/**
 * Returns the fleet's marker or null if not found.
 *
 * @param assetId Integer
 *
 * @return google.maps.Marker|null
 */
export const getFleetMarker = (assetId) => {
  if (fleetMarkers.has(assetId)) {
    return fleetMarkers.get(assetId)
  } else {
    return null
  }
}

/**
 * Remove an existing fleet vehicle's marker.
 *
 * @param assetId Integer
 * @param refreshClustering Boolean
 */
export const removeFleetMarker = (assetId, refreshClustering = false) => {
  if (!fleetMarkers.has(assetId)) return
  let marker = fleetMarkers.get(assetId)

  if (marker.markerLabel !== null) {
    marker.markerLabel.el.parentNode.removeChild(marker.markerLabel.el)
    marker.markerLabel.setMap(null)
    marker.markerLabel.marker = null
    marker.markerLabel = null
    // marker.markerLabelContent = null
  }

  if (marker.safeZone !== null) {
    marker.safeZone.setMap(null)
    marker.safeZone = null
  }

  marker.setMap(null)
  fleetMarkers.delete(assetId)

  if (refreshClustering) {
    updateMarkerCluster()
  }
}

export const removeDetachedMarkers = (excludeMarkers) => {
  const markers = Array.from(fleetMarkers.values())
  markers.forEach((marker) => {
    if (!excludeMarkers.has(marker.assetId)) {
      removeFleetMarker(marker.assetId)
    }
  })

  updateMarkerCluster()
}

/**
 * Removes all fleet markers.
 *
 */
export const clearFleetMarkers = () => {
  removeMarkerCluster()

  fleetMarkers.forEach((marker) => {
    if (marker.markerLabel !== null) {
      marker.markerLabel.el.parentNode.removeChild(marker.markerLabel.el)
      marker.markerLabel.setMap(null)
      marker.markerLabel.marker = null
      marker.markerLabel = null
      // marker.markerLabelContent = null
    }

    marker.setMap(null)
  })

  fleetMarkers.clear()
}

/**
 * Sets the opacity of all markers
 */
export const setAllMarkersOpacity = (opacity) => {
  fleetMarkers.forEach((marker) => {
    marker.setOpacity(opacity)
    if (marker.markerLabel !== null) {
      marker.markerLabel.el.style.opacity = opacity
    }
  })
}

/**
 * Hides all fleet markers by setting their visible property to false.
 */
export const hideFleetMarkers = () => {
  fleetMarkers.forEach((marker) => {
    marker.setVisible(false)
  })
}

/**
 * Shows all fleet markers by setting their visible property to true.
 */
export const showFleetMarkers = () => {
  fleetMarkers.forEach((marker) => {
    marker.setVisible(true)
  })
}

export const updateMarkerCluster = () => {
  const markers = Array.from(fleetMarkers.values())
  if (markerClusterer === null) {
    const algorithm = new SuperClusterAlgorithm({ radius: 250 })

    markerClusterer = new MarkerClusterer({
      map: googleMaps.map,
      markers: markers,
      algorithm: algorithm,
      renderer: {
        render: ({ count, position } /*, stats */) => {
          return new google.maps.Marker({
            position,
            icon: {
              url: getClusterSVG(count),
              scaledSize: new google.maps.Size(clusterWidth, clusterHeight)
            },
            label: {
              text: String(count),
              color: 'white',
              fontSize: `${clusterFontSize}px`
            },
            zIndex: 20
          })

          // marker.addListener('mouseover', () => {
          //   console.log('cluster hovered!')
          // })
          // return marker
        }
      },
      onClusterClick: (event, cluster, map) => {
        // console.log(`DEBUG: onClusterClick`, cluster.marker.position)
        defaultOnClusterClickHandler(event, cluster, map)
        refreshMarkerLabels()
      }
    })
  } else {
    markerClusterer.clearMarkers(true)
    markerClusterer.addMarkers(markers)
    // markerClusterer.render()
  }

  refreshMarkerLabels()
}

export const removeMarkerCluster = () => {
  if (markerClusterer !== null) {
    markerClusterer.clearMarkers(true)
    markerClusterer.setMap(null)
    markerClusterer = null
  }
}

export const refreshMarkerLabels = () => {
  window.setTimeout(() => {
    fleetMarkers.forEach((marker) => {
      if (marker.map) {
        mapUtils.showMarkerLabel(marker)
      } else {
        mapUtils.hideMarkerLabel(marker)
      }
    })
  }, 250)
}



// WEBPACK FOOTER //
// ./src/maps/google/fleetMarkers.js